import { MobileList } from "@dashboard/components/MobileList";
import { formatMoney, formatMoneyRange } from "@dashboard/components/Money";
import { StatusDot } from "@dashboard/components/StatusDot/StatusDot";
import { ProductListQuery } from "@dashboard/graphql";
import useLocale from "@dashboard/hooks/useLocale";
import { RelayToFlat } from "@dashboard/types";
import { Box, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { getProductAvailability } from "./datagrid";
import { messages } from "./messages";

type ProductListItem = RelayToFlat<NonNullable<ProductListQuery["products"]>>[number];

interface ProductListMobileListProps {
  products: ProductListItem[] | undefined;
  loading: boolean;
  selectedChannelId?: string;
  rowAnchor?: (id: string) => string;
  linkState?: unknown;
  onSelectProductIds: (rowsIndex: number[], clearSelection: () => void) => void;
}

const ProductListMobileListItem = ({
  product,
  selectedChannelId,
}: {
  product: ProductListItem;
  selectedChannelId?: string;
}) => {
  const intl = useIntl();
  const { locale } = useLocale();
  const channel = product.channelListings?.find(
    listing => listing.channel.id === selectedChannelId,
  );
  const availability = getProductAvailability(product, intl, channel);
  const from = channel?.pricing?.priceRange?.start?.gross;
  const to = channel?.pricing?.priceRange?.stop?.gross;
  const price =
    from && to
      ? from.amount === to.amount
        ? formatMoney(from, locale)
        : formatMoneyRange(from, to, locale)
      : null;
  const sku = product.variants?.find(variant => variant.sku)?.sku;
  const details = [product.productType?.name, product.category?.name].filter(Boolean).join(" · ");

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
        <Text size={3} fontWeight="medium" style={{ overflowWrap: "anywhere" }}>
          {product.name}
        </Text>
        {price && (
          <Text size={3} fontWeight="bold" style={{ whiteSpace: "nowrap" }}>
            {price}
          </Text>
        )}
      </Box>
      {details && (
        <Text size={2} color="default2" style={{ overflowWrap: "anywhere" }}>
          {details}
        </Text>
      )}
      {sku && (
        <Text size={2} color="default2" style={{ overflowWrap: "anywhere" }}>
          SKU {sku}
        </Text>
      )}
      <Box display="flex" alignItems="center" gap={1.5}>
        <StatusDot status={availability.status} />
        <Text size={2} color="default2">
          {availability.label}
        </Text>
      </Box>
    </Box>
  );
};

export const ProductListMobileList = ({
  products,
  loading,
  selectedChannelId,
  rowAnchor,
  linkState,
  onSelectProductIds,
}: ProductListMobileListProps) => {
  const intl = useIntl();

  return (
    <MobileList
      items={products}
      loading={loading}
      emptyText={intl.formatMessage(messages.emptyText)}
      getKey={product => product.id}
      getHref={rowAnchor ? product => rowAnchor(product.id) : undefined}
      linkState={linkState}
      getThumbnail={product => product.thumbnail?.url}
      onSelectionChange={onSelectProductIds}
      testId="product-list-mobile"
      renderItem={product => (
        <ProductListMobileListItem product={product} selectedChannelId={selectedChannelId} />
      )}
    />
  );
};
